// Framework-neutral editor attachment wire (no antd) — shared by v3 apEditor
// (via handleImageUpload's toast wrapper in apEditorUtils), v4 APRichTextEditor
// and the v4 media kit (APMediaUpload).
import { AppConfig } from '@/config/appConfig';
import { axiosInstance } from '@/providers/dataProvider';
import { useAuthStore } from '@/store/authStore';

// Maximum file size (20MB)
export const MAX_IMAGE_SIZE = 20 * 1024 * 1024;

// Supported image types
export const SUPPORTED_IMAGE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/svg+xml',
];

/**
 * The attachment wire object — byte-identical to what POST /attachments
 * returns and what v3 stores in jsonb (attachments_json.*, resource_json,
 * prompt_attachments…). Unknown fields (convert_status etc.) survive
 * round-trips untouched via the index signature.
 */
export interface APAttachment {
  id: string;
  url: string;
  s3_url?: string;
  name: string;
  type: string;
  uid?: string;
  status?: string;
  updated_at?: string;
  [k: string]: unknown;
}

export interface UploadAttachmentRawOptions {
  /** Extra query-string params (save_record / user_id / record_type / record_id / upload_type). */
  params?: Record<string, string>;
  onProgress?: (percentage: number) => void;
  signal?: AbortSignal;
}

/**
 * Uploads a file to the backend attachments endpoint and resolves with the
 * full attachment wire object. Throws on failure — callers own user-facing
 * error surfacing (antd toast in v3, sonner in v4).
 */
export const uploadAttachmentRaw = async (
  file: File,
  options: UploadAttachmentRawOptions = {}
): Promise<APAttachment> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('name', file.name);

  const token = useAuthStore.getState().token;
  const { apiUrl } = AppConfig;
  const url = `${apiUrl}/attachments`;

  const response = await axiosInstance.post(url, formData, {
    //set timeout to 0 to avoid the timeout error when uploading large files on slow internet connection
    timeout: 0,
    headers: {
      token: token,
      'Content-Type': 'multipart/form-data',
    },
    params: options.params,
    signal: options.signal,
    onUploadProgress: (progressEvent: any) => {
      if (progressEvent.total) {
        const percentage = Math.round((progressEvent.loaded / progressEvent.total) * 100);
        // Call the progress callback if provided
        if (options.onProgress) {
          options.onProgress(percentage);
        }
      } else {
        console.log('📊 Progress event:', {
          loaded: progressEvent.loaded,
          total: 'unknown',
          percentage: 'unknown (backend not sending Content-Length)',
        });
      }
    },
  });

  if (response.data && response.data.url) {
    return response.data as APAttachment;
  }
  console.error('Invalid response from server ==>', response.data);
  throw new Error('Invalid response from server');
};

/**
 * Url-only wrapper over `uploadAttachmentRaw` — the historical editor wire.
 * Behavior-identical for editor consumers (same request, resolves the url).
 */
export const uploadAttachment = async (
  file: File,
  onProgress?: (percentage: number) => void
): Promise<string> => {
  const attachment = await uploadAttachmentRaw(file, { onProgress });
  return attachment.url;
};
